/**
 * VoiceSettings Component
 * Controls for voice parameters (speed, pitch, language)
 */

import React from 'react';
import { FiSliders } from 'react-icons/fi';
import type { VoiceSettings as VoiceSettingsType, VoiceProvider } from '../../types/voice.types';

interface VoiceSettingsProps {
  settings: VoiceSettingsType;
  onChange: (settings: Partial<VoiceSettingsType>) => void;
  provider: VoiceProvider;
  className?: string;
}

export const VoiceSettings: React.FC<VoiceSettingsProps> = ({
  settings,
  onChange,
  provider,
  className = ''
}) => {
  const handleSpeedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ speed: parseFloat(e.target.value) });
  };

  const handlePitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ pitch: parseFloat(e.target.value) });
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ language: e.target.value });
  };

  // Provider-specific capabilities
  const supportsPitch = ['azure', 'google'].includes(provider);
  const supportsLanguage = true; // All providers support language selection

  return (
    <div className={`bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 ${className}`}>
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center gap-2">
          <FiSliders className="text-blue-400" size={20} />
          <h3 className="text-lg font-semibold text-white">Voice Settings</h3>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Speed Control */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label htmlFor="speed" className="text-sm font-medium text-gray-300">
              Speed
            </label>
            <span className="text-sm text-gray-400">{(settings.speed ?? 1.0).toFixed(2)}x</span>
          </div>
          <input
            id="speed"
            type="range"
            min="0.25"
            max="4.0"
            step="0.05"
            value={settings.speed}
            onChange={handleSpeedChange}
            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>Slow (0.25x)</span>
            <span>Normal (1.0x)</span>
            <span>Fast (4.0x)</span>
          </div>
        </div>

        {/* Pitch Control (if supported) */}
        {supportsPitch && (
          <div>
            <div className="flex justify-between items-center mb-2">
              <label htmlFor="pitch" className="text-sm font-medium text-gray-300">
                Pitch
              </label>
              <span className="text-sm text-gray-400">{(settings.pitch ?? 0).toFixed(2)}</span>
            </div>
            <input
              id="pitch"
              type="range"
              min="-20"
              max="20"
              step="1"
              value={settings.pitch}
              onChange={handlePitchChange}
              className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Lower (-20)</span>
              <span>Normal (0)</span>
              <span>Higher (+20)</span>
            </div>
          </div>
        )}

        {/* Language Selection */}
        {supportsLanguage && (
          <div>
            <label htmlFor="language" className="block text-sm font-medium text-gray-300 mb-2">
              Language
            </label>
            <select
              id="language"
              value={settings.language}
              onChange={handleLanguageChange}
              className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="en-US">English (US)</option>
              <option value="en-GB">English (UK)</option>
              <option value="es-ES">Spanish (Spain)</option>
              <option value="es-MX">Spanish (Mexico)</option>
              <option value="fr-FR">French</option>
              <option value="de-DE">German</option>
              <option value="it-IT">Italian</option>
              <option value="pt-BR">Portuguese (Brazil)</option>
              <option value="pt-PT">Portuguese (Portugal)</option>
              <option value="ja-JP">Japanese</option>
              <option value="ko-KR">Korean</option>
              <option value="zh-CN">Chinese (Simplified)</option>
              <option value="zh-TW">Chinese (Traditional)</option>
              <option value="ar-SA">Arabic</option>
              <option value="hi-IN">Hindi</option>
              <option value="ru-RU">Russian</option>
            </select>
          </div>
        )}

        {/* Provider-specific note */}
        {!supportsPitch && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
            <p className="text-xs text-yellow-400">
              Note: {provider} does not support pitch adjustment
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceSettings;
